import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Tag, Flex, Space, Radio, RadioChangeEvent, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';
import {
  ConfirmDeleteModal,
  EditableCell,
  EditableCellProps,
  Table,
  TableColumns,
  TableComponent,
  TableProps,
  ImportTableDataRow,
  notification,
  TableRowBase,
  TableActiveCell,
  getTableFocusSequence,
  getTableRowsErrorsCount,
  getTableColumnsErrorsCount,
  ColumnsEditAll,
  TableEditableCells,
  ValidationFeedback,
  ValueList,
  TableColumn,
  TableColumnType,
  DeleteErrorException,
} from '@ohpen/enso-ui';
import {
  FieldType,
  ImportColumnType,
  ImportSchemaColumn,
  ChangeRequestValidationResponse,
  importsValidateImport,
  importsDeleteImportRows,
  importsUpdateImportCell,
} from '@pmp/api';

import { onGoNext, onGoPrev } from '@pmp/components';
import { getApiErrorMessage } from '@pmp/utils';
import { TableHeaderTitle } from '../../CrudTable/components/TableHeaderTitle';
import { ErrorsTableProps } from '../interfaces';
import { getEditableCells, getSelectedRowsLabel } from '../utils';
import styles from '../styles.module.css';

const { Text } = Typography;

// isValid is an import-wide flag returned on the first page; the following pages only add rows.
const collectRemainingValidationPages = (
  importId: string,
  isValid: boolean,
  previousRowValidations: ValidationFeedback,
  paginationToken?: string | null,
): Promise<{ isValid: boolean; rowValidations: ValidationFeedback }> =>
  paginationToken
    ? importsValidateImport({ path: { importId }, query: { paginationToken } }).then(
        ({ data }: { data: ChangeRequestValidationResponse }) =>
          collectRemainingValidationPages(
            importId,
            isValid,
            { ...previousRowValidations, ...data.rowValidations },
            data.pageOptions.paginationToken,
          ),
      )
    : Promise.resolve({ isValid, rowValidations: previousRowValidations });

export const ErrorsTable = <RowData,>({
  importEntity,
  importId,
  tableData,
  activeCell,
  schema,
  validationFeedback,
  valueLists,
  isLoading,
  readOnly,
  dispatch,
  onDeleteAllRows,
}: ErrorsTableProps<RowData>) => {
  const tableRef = useRef<TableComponent | null>(null);
  const [rowsToDelete, setRowsToDelete] = useState<number[]>([]);
  const [view, setView] = useState<string>('all');
  const [showIds, setShowIds] = useState<boolean>(false);
  const [columnsEditAll, setColumnsEditAll] = useState<ColumnsEditAll>({});
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (validationFeedback !== null || isLoading) return;
    dispatch({ type: 'setIsLoading', isLoading: true });

    importsValidateImport({ path: { importId: importId } })
      .then(({ data }: { data: ChangeRequestValidationResponse }) =>
        collectRemainingValidationPages(importId, data.isValid, data.rowValidations, data.pageOptions.paginationToken),
      )
      .then(({ isValid, rowValidations }: { isValid: boolean; rowValidations: ValidationFeedback }) => {
        dispatch({ type: 'setValidations', validationFeedback: rowValidations, isValid });
      })
      .catch((error: Error) => {
        notification().error({ title: getApiErrorMessage(error) });
        dispatch({ type: 'setValidations', validationFeedback: {}, isValid: false });
        console.error(error);
      })
      .finally(() => {
        dispatch({ type: 'setIsLoading', isLoading: false });
      });
  }, [tableData, importId, dispatch, t, validationFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  const columnKeys: string[] = useMemo(() => schema.map(column => String(column.key)), [schema]);

  const editableCells: TableEditableCells = useMemo(() => getEditableCells(columnKeys, tableData), [columnKeys, tableData]);

  const focusSequence: TableActiveCell[] = useMemo(() => getTableFocusSequence(editableCells), [editableCells]);

  const goToNext = useCallback(() => onGoNext({ focusSequence, activeCell, dispatch }), [focusSequence, activeCell, dispatch]);

  const goToPrev = useCallback(() => onGoPrev({ focusSequence, activeCell, dispatch }), [focusSequence, activeCell, dispatch]);

  const setActiveCell = useCallback(
    (activeCell: TableActiveCell) => {
      dispatch({ type: 'setActiveCell', activeCell });
    },
    [dispatch],
  );

  const handleSave = useCallback(
    (rowNumber: number, rowNumbers: number[], dataIndex: string, value: never, skipNext: boolean): Promise<void> => {
      dispatch({ type: 'setIsValidating', isValidating: true });
      const newData: ImportTableDataRow<RowData>[] = [...tableData];
      const someSelectedRows = selectedRows.length > 0;
      const modifiedRowSelected = selectedRows.includes(rowNumber);
      // Array of rows that have changed
      const updatedRows: ImportTableDataRow<RowData>[] = rowNumbers.reduce((arr: ImportTableDataRow<RowData>[], rowNumber: number) => {
        const currentRowSelected = selectedRows.includes(rowNumber);
        const row: ImportTableDataRow<RowData> | undefined = newData.find(row => row.rowNumber === rowNumber);

        // If the row is not found or the value is the same as before, or there is row selection and the modified row is selected and the current row is not selected
        // or there is row selection and the modified row is not selected and the current row is selected; don't update the cell value and return the array
        if (
          !row ||
          (row.fields as Record<string, string>)[dataIndex] === value ||
          (someSelectedRows && modifiedRowSelected && !currentRowSelected) ||
          (someSelectedRows && !modifiedRowSelected && currentRowSelected)
        )
          return arr;

        // Update the cell value and push the row to the array
        (row.fields as Record<string, string>)[dataIndex] = value;
        return [...arr, row];
      }, []);

      if (!updatedRows.length) {
        skipNext ? setActiveCell(null) : goToNext();
        dispatch({ type: 'setIsValidating', isValidating: false });
        return Promise.resolve();
      }

      dispatch({ type: 'setTableData', tableData: newData });

      skipNext ? setActiveCell(null) : goToNext();

      return importsUpdateImportCell({ path: { importId }, body: { rowNumbers, columnKey: dataIndex, value } }) // TODO send updated rows unflatten
        .then(
          ({
            data: {
              isValid,
              rowValidations,
              pageOptions: { paginationToken },
            },
          }: {
            data: ChangeRequestValidationResponse;
          }) => collectRemainingValidationPages(importId, isValid, rowValidations, paginationToken),
        )
        .then(({ isValid, rowValidations }: { isValid: boolean; rowValidations: ValidationFeedback }) => {
          dispatch({
            type: 'setValidations',
            validationFeedback: { ...validationFeedback, ...rowValidations },
            isValid,
          });
        })
        .catch((err: unknown) => {
          console.error(err);
          dispatch({ type: 'setIsValidating', isValidating: false });
        });
    },
    [dispatch, goToNext, importId, selectedRows, setActiveCell, tableData, validationFeedback],
  );

  const rowsErrorsCount = useMemo(() => getTableRowsErrorsCount(validationFeedback), [validationFeedback]);

  const columnsErrorsCount = useMemo(() => getTableColumnsErrorsCount(validationFeedback), [validationFeedback]);

  const getNumberOfRows = useCallback(() => tableData?.length ?? 0, [tableData]);

  const onChangeShowIds = useCallback((e: RadioChangeEvent) => {
    setShowIds(e.target.value);
  }, []);

  const onChangeSelectedRows = useCallback(
    (selectedRows: TableRowBase<RowData>[]) => setSelectedRows(selectedRows.map(row => row.rowNumber)),
    [],
  );

  const onBulkDelete = useCallback((selectedRows: ImportTableDataRow<RowData>[]) => {
    setRowsToDelete(selectedRows.map(row => row.rowNumber));
  }, []);

  const onCancelDelete = useCallback(() => {
    setRowsToDelete([]);
  }, []);

  const onConfirmDelete: () => Promise<{ description: string }> = useCallback(() => {
    if (!importId || !rowsToDelete.length) return Promise.reject(new DeleteErrorException(t('enso.errors.invalidRecord')));
    dispatch({ type: 'setIsValidating', isValidating: true });
    return importsDeleteImportRows({ path: { importId: importId, rowNumbers: rowsToDelete } })
      .then(({ data }: { data: ChangeRequestValidationResponse }) =>
        collectRemainingValidationPages(importId, data.isValid, data.rowValidations, data.pageOptions.paginationToken),
      )
      .then(({ isValid, rowValidations }: { isValid: boolean; rowValidations: ValidationFeedback }) => {
        const newData = tableData.filter(row => !rowsToDelete.includes(row.rowNumber));

        if (!newData.length) {
          onDeleteAllRows();
          return Promise.reject(new DeleteErrorException(t('global.errors.unexpected')));
        }

        dispatch({ type: 'onDeleteRows', tableData: newData, validationFeedback: rowValidations, isValid });
        return { description: t('global.notifications.deletedSuccessfully') };
      })
      .catch(error => {
        return Promise.reject(new DeleteErrorException(getApiErrorMessage(error)));
      })
      .finally(() => {
        tableRef.current?.resetSelectedRows();
        setRowsToDelete([]);
      });
  }, [dispatch, importId, onDeleteAllRows, rowsToDelete, t, tableData]);

  const handleChangeEditAll = useCallback((columnKey: string) => {
    setColumnsEditAll((prev: ColumnsEditAll) => ({ ...prev, [columnKey]: !prev[columnKey] }));
  }, []);

  const handleViewChange = useCallback(({ target: { value } }: RadioChangeEvent) => {
    setView(value);
  }, []);

  const getRowFeedback = useCallback((rowNumber: number) => validationFeedback?.[rowNumber], [validationFeedback]);

  const getActiveColumnKey = useCallback(
    (rowNumber: number) => (activeCell?.rowNumber === rowNumber ? activeCell.columnKey : null),
    [activeCell],
  );

  const getOnCell = useCallback(
    (column: TableColumn<TableRowBase<RowData>>, title: string, valueList: ValueList, editAll: boolean) =>
      (record: TableRowBase<RowData>) => {
        return {
          key: column.dataIndex ?? 'selection',
          column,
          record,
          valueList,
          title,
          editable: true,
          editableCells,
          editAll,
          showIds,
          mode: 'import',
          goToNext,
          goToPrev,
          setActiveCell,
          getNumberOfRows,
          handleSave,
        };
      },
    [editableCells, getNumberOfRows, goToNext, goToPrev, handleSave, setActiveCell, showIds],
  );

  const formattedColumns: TableColumns<TableRowBase<RowData>> = useMemo(
    () =>
      schema.map((importColumn: ImportSchemaColumn) => {
        const editAll = columnsEditAll[importColumn.key] ?? false;
        const valueList = valueLists[importColumn.type === ImportColumnType.BOOLEAN ? FieldType.YES_NO : (importColumn.listName ?? '')];
        const title = String(importColumn.label);
        const isMultiValueEnumeration = importColumn.type === ImportColumnType.MULTI_VALUE_ENUMERATION;
        const isEnumeration = importColumn.type === ImportColumnType.ENUMERATION || isMultiValueEnumeration;

        function getType(): TableColumnType {
          if (importColumn.type === ImportColumnType.ENUMERATION) return showIds ? 'valueListKey' : 'valueList';
          if (importColumn.type === ImportColumnType.MULTI_VALUE_ENUMERATION) return showIds ? 'multiValueListKey' : 'multiValueList';
          if (importColumn.type === ImportColumnType.MULTI_VALUE_STRING) return 'multiTag';
          if (importColumn.type === ImportColumnType.BOOLEAN) return showIds ? 'booleanKey' : 'boolean';
          if (importColumn.type === ImportColumnType.DATE) return 'date';
          if (importColumn.type === ImportColumnType.NUMBER) return 'number';
          return 'text';
        }

        function getTitle() {
          return (
            <TableHeaderTitle
              columnKey={importColumn.key}
              title={title}
              editAll={editAll}
              disableEditAllKeys={['code']}
              columnsErrorsCount={columnsErrorsCount}
              validationFeedback={validationFeedback}
              handleChangeEditAll={handleChangeEditAll}
            />
          );
        }

        const type = getType();

        const column: TableColumn<TableRowBase<RowData>> = {
          options: {
            listName: importColumn.listName ?? undefined,
            // Firm-reference lists run into the hundreds of options, so the dropdown is unusable
            // without typing to filter.
            allowSearch: isEnumeration,
            sortSelectedFirst: isEnumeration,
            ...(type === 'text' && { editable: { inputProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT) } }),
            format: {
              type,
              // A selection of firm names does not fit a 220px cell, so it stays on one ellipsized
              // line and the rest is reachable through the "+N more" popover.
              ...(isMultiValueEnumeration && { ellipsis: { visibleItems: 2 } }),
            },
          },
          title: getTitle(),
          dataIndex: importColumn.key,
          key: importColumn.key,
          fixed: importColumn.key === 'code' ? 'left' : undefined,
          width: 220,
        };

        return {
          ...column,
          editable: true,
          onCell: getOnCell(column, title, valueList, editAll),
        };
      }),
    [schema, columnsEditAll, valueLists, getOnCell, showIds, columnsErrorsCount, validationFeedback, handleChangeEditAll],
  );

  const visibleColumns: string[] | undefined = useMemo(
    () =>
      formattedColumns
        .filter(({ key }) => (view === 'all' || view === 'rows' ? !!key : !!key && !!columnsErrorsCount[String(key)]))
        .map(({ key }) => String(key)),
    [columnsErrorsCount, formattedColumns, view],
  );

  useEffect(() => {
    const totalRowsErrors = Object.values(rowsErrorsCount).reduce((acc: number, count: number) => acc + (count ? 1 : 0), 0);
    const totalColumnsErrors = Object.keys(columnsErrorsCount).length;
    const totalCellsErrors = Object.values(columnsErrorsCount).reduce((acc: number, count: number) => acc + count, 0);

    if ((view === 'columns' && !totalColumnsErrors) || (view === 'rows' && !totalRowsErrors) || (view === 'cells' && !totalCellsErrors))
      setView('all');
  }, [rowsErrorsCount, columnsErrorsCount, view]);

  const viewOptions = useMemo(() => {
    const totalRowsErrors = Object.values(rowsErrorsCount).reduce((acc: number, count: number) => acc + (count ? 1 : 0), 0);
    const totalColumnsErrors = Object.keys(columnsErrorsCount).length;
    const totalCellsErrors = Object.values(columnsErrorsCount).reduce((acc: number, count: number) => acc + count, 0);

    const getLabel = (label: string, number: number, type: 'error' | 'total' = 'error') =>
      number ? (
        <Space>
          <Text>{t(label)}</Text>
          <Tag color={type === 'total' ? 'green' : 'red'} bordered={false} className={styles.countTag}>
            {number}
          </Tag>
        </Space>
      ) : (
        t(label)
      );

    const options = [
      { value: 'all', label: getLabel('enso.labels.all', tableData.length, 'total') },

      { value: 'columns', label: getLabel('global.labels.columnsWithErrors', totalColumnsErrors), disabled: !totalColumnsErrors },
      { value: 'rows', label: getLabel('enso.labels.rowsWithErrors', totalRowsErrors), disabled: !totalRowsErrors },
      { value: 'cells', label: getLabel('global.labels.cellsWithErrors', totalCellsErrors), disabled: !totalCellsErrors },
    ];
    return options;
  }, [rowsErrorsCount, columnsErrorsCount, tableData.length, t]);

  const showIdOptions = useMemo(
    () => [
      { label: t('global.labels.showLabels'), value: false },
      { label: t('global.labels.showIds'), value: true },
    ],
    [t],
  );

  const filteredDataSource: ImportTableDataRow<RowData>[] = useMemo(
    () =>
      view === 'all' || view === 'columns'
        ? tableData
        : tableData.filter((row: ImportTableDataRow<RowData>) => rowsErrorsCount[row.rowNumber]),
    [rowsErrorsCount, tableData, view],
  );

  const dataSource: TableRowBase<RowData>[] = useMemo(
    () =>
      filteredDataSource.map(
        (row: ImportTableDataRow<RowData>) =>
          ({
            ...row.fields,
            rowNumber: row.rowNumber,
            activeColumnKey: getActiveColumnKey(row.rowNumber),
            rowFeedback: getRowFeedback(row.rowNumber),
          }) as TableRowBase<RowData>,
      ),
    [filteredDataSource, getActiveColumnKey, getRowFeedback],
  );

  const virtual = useMemo(() => dataSource.length > 100, [dataSource.length]);

  const components = useMemo(() => {
    const Wrapper = virtual ? 'div' : 'td';

    return {
      body: {
        row: virtual ? (props: any) => <div {...props} /> : undefined,
        cell: ({
          children,
          column,
          record,
          valueList,
          title,
          editable,
          editableCells,
          editAll,
          showIds,
          mode,
          goToNext,
          goToPrev,
          setActiveCell,
          getNumberOfRows,
          handleSave,
          ...props
        }: EditableCellProps<RowData>) => {
          return !record ? (
            <Wrapper {...props}>{children}</Wrapper>
          ) : (
            <Wrapper {...props}>
              <EditableCell
                column={column}
                record={record}
                valueList={valueList}
                title={title}
                editable={editable}
                editableCells={editableCells}
                editAll={editAll}
                showIds={showIds}
                mode={mode}
                goToNext={goToNext}
                goToPrev={goToPrev}
                setActiveCell={setActiveCell}
                getNumberOfRows={getNumberOfRows}
                handleSave={handleSave}
              >
                {children}
              </EditableCell>
            </Wrapper>
          );
        },
      },
    };
  }, [virtual]);

  const tableProps: TableProps<TableRowBase<RowData>> = useMemo(
    () => ({
      ref: tableRef,
      id: `import-${importEntity}`,
      dataSource,
      columns: formattedColumns,
      rowKey: 'rowNumber',
      pagination: false,
      loading: isLoading || !formattedColumns?.length,
      virtual,
      scroll: { x: 'max-content', y: virtual ? 400 : 'calc(95vh - 410px)' },
      listItemHeight: virtual ? 60 : undefined,
      components,
      className: 'editable-table',
      rowClassName: () => (virtual ? 'editable-row editable-row-60' : 'editable-row'),
      showRowSelection: !readOnly,
      rowSelection: { hideSelectAll: true, fixed: 'left' },
      visibleColumns,
      skeletonLoadingProps: { loading: validationFeedback === null },
      valueLists,
      onChangeSelectedRows,
    }),
    [
      components,
      dataSource,
      formattedColumns,
      importEntity,
      isLoading,
      onChangeSelectedRows,
      readOnly,
      validationFeedback,
      valueLists,
      virtual,
      visibleColumns,
    ],
  );

  return (
    <>
      {dataSource && dataSource.length > 100 && (
        <Flex className={styles.alertRow}>
          <Alert message={t('global.labels.moreThan100rowsSelected', { x: dataSource.length })} type="warning" showIcon closable />
        </Flex>
      )}
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between">
              <Space size="middle">
                <Radio.Group onChange={handleViewChange} options={viewOptions} value={view} optionType="button" />
                <Radio.Group options={showIdOptions} value={showIds} optionType="button" onChange={onChangeShowIds} />
              </Space>
              <Space>
                <span />
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel={getSelectedRowsLabel(importEntity)}>
            <Space size="middle">
              <Table.BulkDeleteButton onClick={onBulkDelete} label={t('enso.buttons.delete')} />
            </Space>
          </Table.BulkBar>
        </>
      </Table>
      <ConfirmDeleteModal open={!!rowsToDelete.length} onConfirm={onConfirmDelete} onCancel={onCancelDelete}>
        <Text>{t('enso.labels.confirmDelete')}</Text>
      </ConfirmDeleteModal>
    </>
  );
};

export default ErrorsTable;
